// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import interpolator from 'natural-spline-interpolator';
import { SheetUpdate, SheetViewColumn, TargetCellsByVariableId } from './editor/sheetEditorTableTypes';
import { FormattingFunctions } from '@providers/FormattingProvider';
import { Rectangle } from 'sheet-happens';
import { Entity, SheetCell, NumberFormatType, SheetRowRepresentation } from '@common/api';
import { CopyCellCommand } from '@data/sheetData/sheetDataActions';

export type SheetSelection = {
    rowStartIdx: number;
    colStartIdx: number;
    rowEndIdx: number;
    colEndIdx: number;
};

function isSelectionValid(selection: SheetSelection) {
    return (
        selection.rowStartIdx !== -1 &&
        selection.colStartIdx !== -1 &&
        selection.rowEndIdx !== -1 &&
        selection.colEndIdx !== -1
    );
}

export function roundValuesToInt(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection
): SheetUpdate[] {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const updates: SheetUpdate[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell || cell.readonly) {
                continue;
            }
            updates.push({
                type: 'cell-update',
                value: Math.round(cell.value?.number || 0),
                rowIndex: i,
                columnIndex,
            });
        }
    }
    return updates;
}

export function setGoal(rows: SheetRowRepresentation[], columnLookup: number[], selection: SheetSelection) {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const updates: SheetUpdate[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell || cell.readonly) {
                continue;
            }

            updates.push({
                type: 'set-goal-update',
                rowIndex: i,
                columnIndex,
            });
        }
    }
    return updates;
}

export function createCopyCellCommands(
    rows: SheetRowRepresentation[],
    columns: SheetViewColumn[],
    columnLookup: number[],
    selection: SheetSelection,
    memberGids: number[],
    delay: number
): CopyCellCommand[] {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const commands: CopyCellCommand[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell || cell.readonly) {
                continue;
            }

            const rowStackKey = `${rows[i].path}-${rows[i].stackPath}`;
            const columnKey = columns[columnIndex].key;

            commands.push({
                sourceMemberGids: memberGids,
                delay,
                destinationCellRowStackKey: rowStackKey,
                destinationCellColumnKey: columnKey,
            });
        }
    }
    return commands;
}

export interface CellKeys {
    rowStackKey: string;
    columnKey: string;
    variableGid?: number;
}

export function getSelectedCellKeys(
    rows: SheetRowRepresentation[],
    columns: SheetViewColumn[],
    columnLookup: number[],
    selection: SheetSelection,
    filter?: (cell: SheetCell) => boolean
): CellKeys[] {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const keys: { rowStackKey: string; columnKey: string; variableGid?: number }[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell) {
                continue;
            }
            if (filter && !filter(cell)) {
                continue;
            }
            const rowStackKey = `${rows[i].path}-${rows[i].stackPath}`;
            const columnKey = columns[columnIndex].key;

            keys.push({
                rowStackKey,
                columnKey,
                variableGid: cell.variableId,
            });
        }
    }
    return keys;
}

export function freezeCells(rows: SheetRowRepresentation[], columnLookup: number[], selection: SheetSelection) {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const updates: SheetUpdate[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell || cell.readonly) {
                continue;
            }

            updates.push({
                type: 'freeze-cells-update',
                rowIndex: i,
                columnIndex,
            });
        }
    }
    return updates;
}

export function setValue(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection,
    newValue: number,
    random: boolean
): SheetUpdate[] {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const updates: SheetUpdate[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell || cell.readonly) {
                continue;
            }

            const isPercent = cell.value?.type === NumberFormatType.Percentage;
            const val = isPercent ? newValue / 100 : newValue;

            const value = random ? Math.random() * 10000 : val;
            updates.push({
                type: 'cell-update',
                value,
                rowIndex: i,
                columnIndex,
            });
        }
    }
    return updates;
}

export function regression(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection
): SheetUpdate[] {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const updates: SheetUpdate[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        const dataset: Array<[number, number]> = [];
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell) {
                continue;
            }
            if (cell.value?.number != null) {
                dataset.push([j, cell.value.number]);
            }
        }
        const f = interpolator(dataset);
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell || cell.readonly) {
                continue;
            }
            updates.push({
                type: 'cell-update',
                value: f(j),
                rowIndex: i,
                columnIndex,
            });
        }
    }
    return updates;
}

export function addPercentToSelection(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection,
    percent: number
): SheetUpdate[] {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const updates: SheetUpdate[] = [];
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const row = rows[i];
        if (!row) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const cell = row.cells[columnIndex];
            if (!cell || cell.readonly) {
                continue;
            }

            updates.push({
                type: 'cell-update',
                value: ((cell.value?.number || 0) * (100 + percent)) / 100,
                rowIndex: i,
                columnIndex,
            });
        }
    }
    return updates;
}

export function copyForPeriods(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection,
    periods: number,
    previous: boolean,
    // TODO: copy for periods with month tags?
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    systemMonths: Entity[]
): SheetUpdate[] {
    if (!isSelectionValid(selection)) {
        return [];
    }
    const { rowStartIdx, rowEndIdx, colStartIdx, colEndIdx } = selection;

    const step = colEndIdx - colStartIdx + 1;
    const updates: SheetUpdate[] = [];
    const changesRequestedPerRow = step * periods;
    let rowChanges = 0;
    for (let r = rowStartIdx; r <= rowEndIdx; r++) {
        const row = rows[r];
        if (!row) {
            continue;
        }
        let currentCol = previous ? colEndIdx - step : colStartIdx + step;
        let templateCol = previous ? colEndIdx : colStartIdx;

        while (rowChanges < changesRequestedPerRow) {
            const columnIndex = columnLookup[currentCol];
            const templateIndex = columnLookup[templateCol];

            const thisItem = row.cells[columnIndex];
            const templateItem = row.cells[templateIndex];
            if (!templateItem || !thisItem || !thisItem /* || !templateItem.dimensionIds */) {
                break;
            }

            const thisItemHasMonthTag = false;
            /*             for (const tagId of thisItem.dimensionIds) {
                if (systemMonths.find((t) => t.id === tagId)) {
                    thisItemHasMonthTag = true;
                    break;
                }
            } */

            const templateItemHasMonthTag = false;
            /*             for (const tagId of templateItem.dimensionIds) {
                if (systemMonths.find((t) => t.id === tagId)) {
                    templateItemHasMonthTag = true;
                    break;
                }
            } */

            if (!thisItemHasMonthTag || !templateItemHasMonthTag) {
                if (previous) currentCol--;
                else currentCol++;
                continue;
            }
            if (thisItem.readonly /* || thisItem.dimensionIds === undefined */) {
                if (previous) currentCol--;
                else currentCol++;
                rowChanges++;
                continue;
            }

            updates.push({
                type: 'cell-update',
                value: templateItem.value?.number || null,
                rowIndex: r,
                columnIndex,
            });

            if (previous) currentCol--;
            else currentCol++;

            if (previous) templateCol--;
            else templateCol++;

            rowChanges++;

            if (!previous && templateCol > colEndIdx) {
                templateCol = colStartIdx;
            }
            if (previous && templateCol < colStartIdx) {
                templateCol = colEndIdx;
            }
        }

        rowChanges = 0;
    }
    return updates;
}

export function getTargetCellsByVariableId(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection
): TargetCellsByVariableId {
    const { colEndIdx, colStartIdx, rowEndIdx, rowStartIdx } = selection;
    const targetCellsByVariableId = new Map<number, number[][]>();

    for (let r = rowStartIdx; r <= rowEndIdx; r++) {
        const row = rows[r];
        if (row === undefined) {
            continue;
        }

        for (let c = colStartIdx; c <= colEndIdx; c++) {
            const cell = row.cells[columnLookup[c]];
            if (cell === undefined) {
                continue;
            }

            const cellTagIds: number[] = /* cell.dimensionIds || */ [];
            const variableId = cell.variableId;
            if (!variableId) {
                continue;
            }

            if (targetCellsByVariableId.has(variableId)) {
                const targetCells = targetCellsByVariableId.get(variableId) || [];
                targetCells.push(cellTagIds);
                targetCellsByVariableId.set(variableId, targetCells);
            } else {
                targetCellsByVariableId.set(variableId, [cellTagIds]);
            }
        }
    }
    return targetCellsByVariableId;
}

export function getTargetCellsArray(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection
): number[][] {
    const targetCells: number[][] = [];
    if (!isSelectionValid(selection)) {
        return targetCells;
    }
    for (let i = selection.rowStartIdx; i <= selection.rowEndIdx; i++) {
        const tableRow = rows[i];
        if (!tableRow) {
            continue;
        }
        for (let j = selection.colStartIdx; j <= selection.colEndIdx; j++) {
            const columnIndex = columnLookup[j];
            const item = tableRow.cells[columnIndex];
            if (!item) {
                continue;
            }
            if (item.readonly) {
                continue;
            }
            // if (item.dimensionIds) {
            //     targetCells.push(item.dimensionIds);
            // }
        }
    }
    return targetCells;
}

export function getSelectedRows(rows: SheetRowRepresentation[], selection: SheetSelection): SheetRowRepresentation[] {
    const { rowStartIdx, rowEndIdx } = selection;
    // use map because row can be generated from table and there can be multiple rows with same id
    const selectedRowsMap = new Map<number, SheetRowRepresentation>();
    for (let i = rowStartIdx; i <= rowEndIdx; i++) {
        const row = rows[i];
        if (row) {
            selectedRowsMap.set(row.id, row);
        }
    }
    return Array.from(selectedRowsMap.values());
}

export function getSelectedValuesInfo(
    rows: SheetRowRepresentation[],
    columnLookup: number[],
    selection: SheetSelection,
    formatting: FormattingFunctions
): { avg: string; sum: string; count: string } {
    const { formatNumber } = formatting;
    const { rowStartIdx, colStartIdx, rowEndIdx, colEndIdx } = selection;

    let selectionNorm = 0;
    let selectionCount = 0;
    let selectionSum = 0;

    for (let i = rowStartIdx; i <= rowEndIdx; i++) {
        const tableRow = rows[i];
        if (!tableRow) {
            continue;
        }

        for (let j = colStartIdx; j <= colEndIdx; j++) {
            const columnIndex = columnLookup[j];

            const item = tableRow.cells[columnIndex];
            if (!item) {
                continue;
            }
            selectionCount++;

            if (item.value?.type !== NumberFormatType.Date && item.value?.number != null) {
                selectionNorm++;
                selectionSum += item.value.number;
            }
        }
    }

    const selectionAvg = selectionNorm ? selectionSum / selectionNorm : 0;

    return {
        count: formatNumber(selectionCount),
        avg: formatNumber(selectionAvg, '#,##0.00'),
        sum: formatNumber(selectionSum, '#,##0.00'),
    };
}

// Keep only top level of mixed-level selections
export const getTopLevelRows = (rows: SheetRowRepresentation[]) => {
    const minDepth = rows.reduce((depth, row) => Math.min(depth, row.depth), Infinity);
    return rows.filter((row) => row.depth === minDepth);
};

export const toRowSelection = ({ rowStartIdx, rowEndIdx }: SheetSelection): Rectangle => [
    [-1, rowStartIdx],
    [-1, rowEndIdx],
];

export function createMessageAnchorForSheetRowCell(clusterPath: string, anchor: string) {
    return `CLUSTER:${clusterPath}//${anchor}`;
}
